import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createCompany, CreateCompanyPayload } from 'services/clients/user';
import { SignUpTemplate } from 'layouts/SignUpTemplate';
import { useSegment } from 'common/hooks/segment';
import { useUserContext } from 'common/contexts/UserContext';
import { fetchPortCollections, PortCollectionQueryKeys } from 'services/clients/portCollection';
import { CompanyCreation } from './CompanyCreation';
import { CompanyDetails } from './CompanyDetails';
import { AddYourTeam } from './AddYourTeam';
import { companyTypes } from './CompanyCreation/types';
import { useCreateCompanyState } from './utility';
import { AlreadyCreated } from './AlreadyCreated';

export const CreateCompany: React.FC = () => {
  const { user, fetchUser } = useUserContext();
  const [state, setState] = useCreateCompanyState();

  // Prefetch PortCollections
  useQuery(
    [PortCollectionQueryKeys.PortCollections],
    ({ signal }) => fetchPortCollections({ pageSize: 100, isVisible: true }, signal),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const createCompanyMutation = useMutation(createCompany);

  const { track } = useSegment();

  let stage;
  switch (state.stage) {
    case 'detail-company':
      stage = (
        <CompanyDetails
          submit={async (values) => {
            const { name, scac, type, autoJoinEmailDomain, otherType } = state;
            const { portCollections, volume } = values;
            const payload: CreateCompanyPayload = {
              name,
              company_type_detail: type,
              port_collection_codes: portCollections,
              expected_container_volume: volume,
              auto_join_email_domain: autoJoinEmailDomain,
              other_type: otherType,
            };
            const companyType = companyTypes.find((c) => c.key === type);
            if (companyType?.fields?.includes('scac')) {
              payload.scac = scac;
            }
            await createCompanyMutation.mutateAsync(payload, {
              onSuccess: () => {
                track(
                  'User qualifies company',
                  {
                    success: true,
                    error: '',
                    companyName: payload.name,
                    companyType: payload.company_type_detail,
                    scac: payload.scac || '',
                    otherType: payload.other_type || '',
                    autoJoinEmailDomain: payload.auto_join_email_domain,
                    portCollectionCodes: payload.port_collection_codes,
                    containerVolume: payload.expected_container_volume,
                  },
                  { slack: true, includeUser: true }
                );

                // fetch the user to get the updated version with the company object
                fetchUser();
                setState({ stage: 'invite', type });
              },
              // eslint-disable-next-line
              onError: (error: any) => {
                // eslint-disable-next-line
                console.error(error);
                track(
                  'User qualifies company',
                  {
                    success: false,
                    error: '',
                    companyName: payload.name,
                    companyType: payload.company_type_detail,
                    scac: payload.scac || '',
                    otherType: payload.other_type || '',
                    autoJoinEmailDomain: payload.auto_join_email_domain,
                    portCollectionCodes: payload.port_collection_codes,
                    expectedContainerVolume: payload.expected_container_volume,
                  },
                  { slack: true, includeUser: true }
                );
              },
            });
          }}
          companyType={state.type}
        />
      );
      break;
    case 'invite':
      stage = <AddYourTeam companyType={state.type} />;
      break;
    case 'create-company':
    default:
      stage = (
        <CompanyCreation
          submit={(values) => {
            track(
              'User submitted company type and name',
              {
                companyName: values.name,
                companyType: values.type,
                scac: values.scac || '',
                otherType: values.otherType || '',
                autoJoinEmailDomain: values.autoJoinEmailDomain,
              },
              { includeUser: true }
            );
            // First replace to enable support for back navigation
            setState({ ...values, stage: 'create-company' }, { replace: true });
            // Then proceed
            setState({ ...values, stage: 'detail-company' });
          }}
        />
      );
      break;
  }

  if (state.stage !== 'invite' && !!user?.company) {
    stage = <AlreadyCreated />;
  }

  return (
    <SignUpTemplate>
      <div className="flex flex-col items-center">{stage}</div>
    </SignUpTemplate>
  );
};
